/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Box,
  Button,
  Circle,
  Code,
  Collapse,
  Heading,
  Icon,
  IconButton,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  Text,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { IoReload } from "react-icons/io5";
import LoadingSpinner from "../../LoadingSpinner";

export default function StepDone({
  onReset,
  importId,
  getRosterImportResults,
  getRostersHistory,
}) {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const { isOpen, onToggle } = useDisclosure();

  const fetchResults = async () => {
    if (importId) {
      try {
        const data = await getRosterImportResults(importId);
        setResults(data);
      } catch (err) {
        console.error("Failed to fetch results:", err);
      } finally {
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchResults();
  }, [importId, getRosterImportResults]);

  // Derive status — check both root-level status and orchestration_status.runtimeStatus
  const rootStatus = results?.status ?? "";
  const orchestrationStatus =
    results?.orchestration_status?.runtimeStatus ?? "";
  const isFailed =
    rootStatus.toLowerCase() === "failed" || orchestrationStatus === "Failed";
  const isSuccess =
    rootStatus.toLowerCase() === "completed" ||
    orchestrationStatus === "Completed";

  const totalRows = results?.total_rows ?? 0;
  const currentRows = results?.current_rows ?? 0;

  const rawOutput = results?.orchestration_status?.output ?? "";
  const errorMatch = rawOutput.match(/TypeError:[^\n]+/);
  const errorMessage = errorMatch ? errorMatch[0] : rawOutput.slice(0, 200);

  const activityMatch = rawOutput.match(/Activity function '([^']+)'/);
  const failedActivity = activityMatch ? activityMatch[1] : null;

  const formatTimestamp = (ts) => {
    if (!ts) return "—";
    const num = Number(ts);
    if (isNaN(num)) return ts;
    return new Date(num * 1000).toLocaleString();
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <VStack spacing={6} py={6} textAlign="center" position="relative">
      <IconButton
        icon={<Icon as={IoReload} />}
        size="lg"
        borderRadius="full"
        bg="none"
        color="black"
        border="2px solid"
        borderColor={isFailed ? "red.200" : "green.200"}
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        gap={2}
        onClick={fetchResults}
        position="absolute"
        top={4}
        right={4}
        _hover={{ bg: isFailed ? "red.50" : "green.50" }}
        _active={{ bg: isFailed ? "red.100" : "green.100" }}
      />
      {/* Status icon */}
      <Circle
        size="72px"
        bg={isFailed ? "red.50" : "green.50"}
        color={isFailed ? "red.500" : "green.500"}
        border="2px solid"
        borderColor={isFailed ? "red.200" : "green.200"}
        fontSize="3xl"
      >
        {isFailed ? "✕" : "✓"}
      </Circle>

      {/* Title */}
      <Box>
        <Heading size="lg" color="gray.800" mb={2}>
          {isFailed ? "Import Failed" : "Import Complete!"}
        </Heading>
        <Text color="gray.500" fontSize="sm">
          {isFailed
            ? "The import could not be completed. See details below."
            : "Your file was imported successfully."}
        </Text>
      </Box>

      {/* Stats — show only what the API returns */}
      <SimpleGrid columns={2} spacing={4} w="full">
        <Stat bg="gray.50" borderRadius="xl" p={4} textAlign="center">
          <StatNumber fontSize="2xl" color="gray.700">
            {totalRows.toLocaleString()}
          </StatNumber>
          <StatLabel fontSize="xs" textTransform="uppercase" color="gray.400">
            Total Rows
          </StatLabel>
        </Stat>
        <Stat bg="gray.50" borderRadius="xl" p={4} textAlign="center">
          <StatNumber fontSize="2xl" color="gray.700">
            {currentRows.toLocaleString()}
          </StatNumber>
          <StatLabel fontSize="xs" textTransform="uppercase" color="gray.400">
            Rows Processed
          </StatLabel>
        </Stat>
      </SimpleGrid>

      {/* Error detail (failure only) */}
      {isFailed && rawOutput && (
        <Alert
          status="error"
          borderRadius="xl"
          flexDirection="column"
          alignItems="flex-start"
          textAlign="left"
          gap={2}
        >
          <AlertTitle fontSize="sm">
            {failedActivity
              ? `Failed at: ${failedActivity}`
              : "Orchestration Error"}
          </AlertTitle>
          <AlertDescription fontSize="xs" color="red.700">
            {errorMessage}
          </AlertDescription>

          {/* Collapsible raw trace */}
          <Button
            size="xs"
            variant="ghost"
            colorScheme="red"
            onClick={onToggle}
            px={0}
            mt={1}
          >
            {isOpen ? "Hide" : "Show"} full trace
          </Button>
          <Collapse in={isOpen} animateOpacity>
            <Code
              display="block"
              whiteSpace="pre-wrap"
              fontSize="10px"
              p={3}
              borderRadius="md"
              bg="red.50"
              color="red.800"
              maxH="180px"
              overflowY="auto"
              w="full"
            >
              {rawOutput}
            </Code>
          </Collapse>
        </Alert>
      )}

      {/* Metadata */}
      {results && (
        <Box w="full" bg="gray.50" borderRadius="xl" p={4} textAlign="left">
          <SimpleGrid columns={2} spacing={2}>
            {[
              ["File", results.blobName],
              ["Roster", results.roster_id],
              ["Total Rows", totalRows],
              ["Created By", results.createdBy],
              ["Started", formatTimestamp(results.createdAt)],
              ["Completed", formatTimestamp(results.completed_at)],
              ["Status", rootStatus || orchestrationStatus || "—"],
            ].map(([label, value]) => (
              <Box key={label}>
                <Text
                  fontSize="10px"
                  color="gray.400"
                  textTransform="uppercase"
                >
                  {label}
                </Text>
                <Text
                  fontSize="xs"
                  color="gray.700"
                  fontWeight="500"
                  isTruncated
                >
                  {value ?? "—"}
                </Text>
              </Box>
            ))}
          </SimpleGrid>
        </Box>
      )}

      <Button
        size="sm"
        bg={isFailed ? "red.500" : "teal.500"}
        color="white"
        borderRadius="sm"
        fontWeight="500"
        px={5}
        _hover={{ bg: isFailed ? "red.600" : "teal.600" }}
        _active={{ bg: isFailed ? "red.700" : "teal.700" }}
        border="none"
        onClick={onReset}
        w="full"
        mt={2}
      >
        {isFailed ? "Try Again" : "Start New Import"}
      </Button>
    </VStack>
  );
}
